import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { useGetWarehouseInspectionProcurement } from '@/hooks/procurement/use-get-procurements';
import WarehouseInspectionHeader from '@/components/warehouse/inspection/WarehouseInspectionHeader';
import ProcurementDetailsCard from '@/components/warehouse/inspection/ProcurementDetailsCard';
import InspectionFormCard, { InspectionData } from '@/components/warehouse/inspection/InspectionFormCard';
import ItemWarehouseReceivingForm from '@/components/warehouse/inspection/ItemWarehouseReceivingForm';
import { ItemWarehouseData } from '@/types/database/procurement-item-warehouse';
import ProcurementNotFound from '@/components/warehouse/inspection/ProcurementNotFound';
import WarehouseInspectionSkeleton from '@/components/warehouse/inspection/WarehouseInspectionSkeleton';
import { useWarehouseInspectionActions } from '@/components/warehouse/inspection/hooks/useWarehouseInspectionActions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle } from 'lucide-react';
import { WAREHOUSE_REJECTION_REASONS } from '@/types/database.types';

const WarehouseInspection = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { data: procurement, isLoading } = useGetWarehouseInspectionProcurement(id || '');
  
  // Updated to use receivedQuantityKg instead of netKgPayable
  const [inspectionData, setInspectionData] = useState<InspectionData>({
    receivedQuantityKg: 0,
    inspectionNotes: '',
    rejectionReason: ''
  });

  // Item-level warehouse data for multi-item procurements
  const [itemWarehouseData, setItemWarehouseData] = useState<ItemWarehouseData[]>([]);

  const isMultiItem = (procurement?.items?.length || 0) > 1;

  // Get quality compensation from quality inspection
  const getQualityCompensation = (): number => {
    if (!procurement) return 0;
    const extendedProcurement = procurement as any;
    // Use final compensation if available, otherwise preliminary
    return extendedProcurement?.quality?.compensation_kg 
      || extendedProcurement?.quality?.preliminary_compensation_kg 
      || 0;
  };

  const compensationKg = getQualityCompensation();
  const expectedQuantity = procurement?.total_quantity || 0;

  // Initialize item warehouse data when procurement loads
  useEffect(() => {
    if (procurement?.items && procurement.items.length > 1) {
      const extendedProcurement = procurement as any;
      const itemQualities = extendedProcurement?.item_qualities || [];
      
      setItemWarehouseData(procurement.items.map(item => {
        // Find matching item quality data
        const itemQuality = itemQualities.find((iq: any) => iq.procurement_item_id === item.id);
        const itemCompensationKg = itemQuality?.compensation_kg || 0;
        const isRejected = itemQuality?.is_rejected || false;
        
        // If item is rejected at quality, set received and net payable to 0
        const defaultReceived = isRejected ? 0 : item.quantity;
        const defaultNetPayable = isRejected ? 0 : Math.max(0, item.quantity - itemCompensationKg);
        
        return {
          itemId: item.id,
          productName: item.product_name,
          expectedQuantity: item.quantity,
          qualityGrade: itemQuality?.quality_grade || item.quality_grade,
          compensationKg: itemCompensationKg,
          receivedQuantityKg: defaultReceived,
          netPayableKg: defaultNetPayable,
          notes: '',
          isRejected // Pass through rejection flag
        } as ItemWarehouseData;
      }));
    }
  }, [procurement?.items]);

  // Initialize single-item received quantity when procurement loads
  useEffect(() => {
    if (procurement && !isMultiItem) {
      setInspectionData(prev => ({
        ...prev,
        receivedQuantityKg: procurement.total_quantity || 0
      }));
    }
  }, [procurement, isMultiItem]);

  // Sync multi-item quantities to main inspection data
  useEffect(() => {
    if (isMultiItem && itemWarehouseData.length > 0) {
      const totalReceived = itemWarehouseData.reduce(
        (sum, item) => sum + (item.receivedQuantityKg || 0), 
        0
      );
      const totalNetPayable = itemWarehouseData.reduce(
        (sum, item) => sum + (item.netPayableKg || 0), 
        0
      );
      setInspectionData(prev => ({
        ...prev,
        receivedQuantityKg: totalReceived
      }));
      console.log('[WAREHOUSE] Multi-item sync:', { totalReceived, totalNetPayable, itemCount: itemWarehouseData.length });
    }
  }, [itemWarehouseData, isMultiItem]);

  const { handleConfirm, handleReject, isSubmitting } = useWarehouseInspectionActions(
    procurement, 
    itemWarehouseData
  );

  const handleBack = () => {
    navigate('/warehouse');
  };

  const onConfirm = () => handleConfirm(inspectionData);
  const onReject = () => handleReject(inspectionData);

  if (isLoading) {
    return (
      <Layout fullWidth>
        <div className="p-6">
          <WarehouseInspectionSkeleton />
        </div>
      </Layout>
    );
  }

  if (!procurement) {
    return (
      <Layout fullWidth>
        <div className="p-6 space-y-6">
          <WarehouseInspectionHeader 
            onBack={handleBack} 
            procurementId="unknown" 
          />
          <ProcurementNotFound />
        </div>
      </Layout>
    );
  }

  return (
    <Layout fullWidth>
      <div className="p-6 space-y-6">
        <WarehouseInspectionHeader 
          onBack={handleBack} 
          procurementId={procurement.id} 
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProcurementDetailsCard procurement={procurement} />
          
          {isMultiItem ? (
            // Multi-item warehouse receiving form
            <Card>
              <CardHeader>
                <CardTitle>Warehouse Receiving Form</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Per-Item Warehouse Receiving */}
                <ItemWarehouseReceivingForm
                  itemWarehouseData={itemWarehouseData}
                  setItemWarehouseData={setItemWarehouseData}
                />

                <div className="space-y-2">
                  <Label htmlFor="inspectionNotes">Overall Inspection Notes</Label>
                  <Textarea
                    id="inspectionNotes"
                    value={inspectionData.inspectionNotes}
                    onChange={(e) =>
                      setInspectionData(prev => ({ ...prev, inspectionNotes: e.target.value }))
                    }
                    placeholder="Add any inspection observations..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rejectionReason">Rejection Reason (if rejecting)</Label>
                  <Select
                    value={inspectionData.rejectionReason}
                    onValueChange={(value) =>
                      setInspectionData(prev => ({ ...prev, rejectionReason: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select rejection reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(WAREHOUSE_REJECTION_REASONS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={onConfirm}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirm
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={onReject}
                    disabled={isSubmitting || !inspectionData.rejectionReason}
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            // Single-item uses the updated form with received quantity
            <InspectionFormCard
              inspectionData={inspectionData}
              setInspectionData={setInspectionData}
              onConfirm={onConfirm}
              onReject={onReject}
              isSubmitting={isSubmitting}
              expectedQuantity={expectedQuantity}
              compensationKg={compensationKg}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default WarehouseInspection;
